import { useI18n, type MessageKey } from '@ecom/i18n';
import { Button } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { buildPublicShopUrl, normalizeStorefrontBaseUrl } from '../share-links';
import { resolveShareState, type ShareState } from '../share-state';

import { useProductsQuery } from '@/features/products/api';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { useMyStoreQuery } from '@/features/store/api';
import { env } from '@/lib/env';

/** States in which the public shop answers (the tenant is active): an empty catalogue is still a reachable page. */
const OPENABLE: ReadonlySet<ShareState> = new Set(['ready', 'no-product', 'unpublished']);

const WHY_NOT: Partial<
  Record<
    ShareState,
    {
      readonly textKey: MessageKey;
      readonly cta?: { readonly labelKey: MessageKey; readonly to: string };
    }
  >
> = {
  'no-store': {
    textKey: 'merchant.viewShop.noStore',
    cta: { labelKey: 'merchant.share.noStore.cta', to: '/store' },
  },
  pending: { textKey: 'merchant.share.pending.text' },
  suspended: { textKey: 'merchant.share.suspended.text' },
  refused: { textKey: 'merchant.share.refused.text' },
};

// A white button on the stone frame (« Chechia »): each screen keeps the one ink primary for its own main job.
const LINK_BUTTON =
  'ecom-button ecom-button--secondary ecom-button--sm max-sm:h-9 max-sm:w-9 max-sm:px-0';

/**
 * « Voir la boutique » — opens the merchant's OWN public shop in a new tab.
 *
 * The address is built exactly like « Partager ma boutique » (`buildPublicShopUrl`): the storefront base from the
 * deployment (`VITE_STOREFRONT_BASE_URL`), the dashboard's locale and the slug of the shop returned by
 * `GET /merchant/store` — which the API scopes to the authenticated shop. Nothing comes from the URL bar, so there is
 * no other shop to reach by editing it. Until the shop is publicly reachable (no shop yet, account pending, suspended
 * or refused) the button explains why instead of opening a page that would answer « not found ».
 */
export function ViewShopButton(): ReactElement | null {
  const { t, locale } = useI18n();
  const shop = useActiveShop();
  const storeQuery = useMyStoreQuery();
  const productsQuery = useProductsQuery();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent | KeyboardEvent): void => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false);
        return;
      }
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const state = resolveShareState({
    store: storeQuery.data,
    storeFailed: storeQuery.isError,
    tenantStatus: shop.activeMembership?.tenantStatus,
    products: productsQuery.data,
    productsFailed: productsQuery.isError,
  });
  const baseUrl = normalizeStorefrontBaseUrl(env.storefrontBaseUrl);
  // The deployment did not say where the storefront lives: no button is better than a wrong link.
  if (baseUrl === null) return null;

  const label = t('merchant.viewShop.label');
  // Below 640 px the bar is narrow: the storefront glyph alone, the full label staying the accessible name.
  const shown = (
    <>
      {/* A phone's bar keeps room for the shop name and the live status: the storefront glyph alone, the full label
          staying the accessible name. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-4 w-4 sm:hidden"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 9l1.5-5h13L20 9M4 9h16v2a3 3 0 0 1-5.33 1.9A3 3 0 0 1 12 14a3 3 0 0 1-2.67-1.1A3 3 0 0 1 4 11zM5 14v6h14v-6" />
      </svg>
      <span className="hidden sm:inline">{label}</span>
      <span className="sr-only sm:hidden">{label}</span>
    </>
  );

  if (state === 'loading' || state === 'unavailable') {
    return (
      <Button variant="secondary" size="sm" disabled>
        {shown}
      </Button>
    );
  }

  const url = buildPublicShopUrl(baseUrl, locale, storeQuery.data?.slug);
  if (OPENABLE.has(state) && url !== null) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_BUTTON}
        title={t('merchant.share.newTab')}
      >
        {shown}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="hidden h-3.5 w-3.5 sm:block rtl:-scale-x-100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M7 17 17 7M9 7h8v8" />
        </svg>
      </a>
    );
  }

  const why = WHY_NOT[state];
  return (
    <div ref={wrapperRef} className="relative">
      <Button
        variant="secondary"
        size="sm"
        className="max-sm:h-9 max-sm:w-9 max-sm:px-0"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {shown}
      </Button>
      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label={label}
          className="souk-popover absolute end-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-[14px] border border-line bg-surface p-3 text-sm text-ink shadow-popover"
        >
          <p className="font-medium">{t('merchant.viewShop.notPublic')}</p>
          {why !== undefined ? <p className="mt-1 text-inkSoft">{t(why.textKey)}</p> : null}
          {why?.cta !== undefined ? (
            <Link
              to={why.cta.to}
              className="mt-2 inline-block font-medium text-accent underline"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t(why.cta.labelKey)}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
